import React, { useEffect, useMemo, useState } from "react";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { Autocomplete } from "@material-ui/lab";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { moveScansToDeploymentSale, SaleAction } from "actions";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Button, SecondaryButton } from "components/Form";
import { Label } from "components/Form/FormikControls";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import {
  buildMoveScansToSaleOptions,
  SaleOption,
} from "components/MoveScansToSale/lib";

import { ModalTypes } from "constants/navigation";

import {
  getConsignableSales,
  getConsignableSalesIsFetching,
  getContextByModalType,
  getCurrentSale,
  getSales,
  getSpecies,
  selectIsSoldBySaleLotIdLookup,
  selectSaleLotByEidLookup,
} from "selectors";
import { pluralize } from "lib/pluralize";

interface MoveScansToSaleDialogContext {
  scans?: Scan[];
}

interface MoveScansToSaleDialogProps {
  onClose: () => void;
}

const SaleSelectWrapper = styled.div`
  width: 100%;
`;

const SaleOptionCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray78};
  border-radius: 2px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.titleDark};
  padding: ${({ theme }) => theme.space[2]}px;
  width: 100%;
`;

const SaleOptionTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const SaleOptionDetail = styled.div`
  color: ${({ theme }) => theme.colors.gray40};
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
`;

function renderSaleOption(saleOption: SaleOption) {
  return (
    <SaleOptionCard>
      <SaleOptionTitle>{saleOption.title}</SaleOptionTitle>
      <SaleOptionDetail>{saleOption.detail}</SaleOptionDetail>
    </SaleOptionCard>
  );
}

export function MoveScansToSaleDialog({ onClose }: MoveScansToSaleDialogProps) {
  const dispatch = useDispatch();
  const context =
    useSelector<RootState, MoveScansToSaleDialogContext | undefined>(
      getContextByModalType(ModalTypes.MoveScansToSale),
    ) || {};
  const { scans = [] } = context;
  const consignableSales = useSelector<
    RootState,
    Record<string | number, ConsignableSale>
  >(getConsignableSales);
  const ownSales = useSelector<RootState, Record<string | number, Sale>>(
    getSales,
  );
  const isFetchingConsignableSales = useSelector<RootState, boolean>(
    getConsignableSalesIsFetching,
  );
  const currentSale = useSelector<RootState, Sale | null>(getCurrentSale);
  const speciesById = useSelector<RootState, Record<number, Species>>(
    getSpecies,
  );
  const saleLotByEidLookup = useSelector<
    RootState,
    Record<string, SaleLot | SaleyardScanSaleLot | undefined>
  >(selectSaleLotByEidLookup);
  const isSoldBySaleLotIdLookup = useSelector<
    RootState,
    Record<string, boolean>
  >(selectIsSoldBySaleLotIdLookup);
  const [selectedDeploymentSaleId, setSelectedDeploymentSaleId] = useState<
    number | null
  >(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    dispatch(SaleAction.fetchConsignableSales({}));
    dispatch(SaleAction.request({ fetchAll: true }));
  }, [dispatch]);

  const saleOptions = useMemo<SaleOption[]>(
    () =>
      buildMoveScansToSaleOptions({
        consignableSales,
        currentLivestockSaleId: currentSale?.livestocksale_id || null,
        ownSales,
        speciesById,
      }),
    [consignableSales, currentSale?.livestocksale_id, ownSales, speciesById],
  );

  const selectedSaleOption = saleOptions.find(
    saleOption => saleOption.value === selectedDeploymentSaleId,
  );

  const copiedScanCount = scans.filter(scan => {
    const saleLot = saleLotByEidLookup[scan.EID];
    return Boolean(saleLot?.id && isSoldBySaleLotIdLookup[saleLot.id]);
  }).length;
  const movedScanCount = scans.length - copiedScanCount;
  const copiedScanLabel =
    copiedScanCount === 1 ? "animal/scan" : "animals/scans";
  const confirmButtonMessage =
    copiedScanCount > 0 && movedScanCount > 0
      ? "Copy/Move Scans"
      : copiedScanCount > 0
        ? "Copy Scans"
        : "Move Scans";

  const confirmMessage =
    copiedScanCount > 0
      ? [
          `${copiedScanCount} ${copiedScanLabel} will be copied to ${
            selectedSaleOption?.label
          } because ${copiedScanCount === 1 ? "it has" : "they have"} been sold in the current sale.`,
          movedScanCount > 0
            ? `${movedScanCount} ${pluralize(
                "scan",
                movedScanCount,
              )} will be moved to ${selectedSaleOption?.label}.`
            : null,
        ]
          .filter(Boolean)
          .join("\n\n")
      : `${scans.length} ${pluralize(
          "scan",
          scans.length,
        )} will be moved to ${selectedSaleOption?.label}.`;

  function onSubmit() {
    setIsConfirmOpen(true);
  }

  function onConfirmMoveScans() {
    if (selectedDeploymentSaleId === null || scans.length === 0) {
      return;
    }

    dispatch(moveScansToDeploymentSale(selectedDeploymentSaleId, scans));
    setIsConfirmOpen(false);
    onClose();
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle onClose={onClose}>Move Scans To Sale</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {isFetchingConsignableSales && (
              <Grid item xs={12}>
                Loading sales...
              </Grid>
            )}
            {!isFetchingConsignableSales && saleOptions.length === 0 && (
              <Grid item xs={12}>
                No sales are available.
              </Grid>
            )}
            {!isFetchingConsignableSales && saleOptions.length > 0 && (
              <Grid item xs={12}>
                <SaleSelectWrapper>
                  <Label tooltip="Only future sales of the same species are available to be selected.">
                    Sale
                  </Label>
                  <Autocomplete
                    autoHighlight
                    getOptionLabel={(saleOption: SaleOption) =>
                      saleOption.label
                    }
                    getOptionSelected={(
                      saleOption: SaleOption,
                      value: SaleOption,
                    ) => saleOption.value === value.value}
                    onChange={(ignored, saleOption: SaleOption | null): void =>
                      setSelectedDeploymentSaleId(saleOption?.value || null)
                    }
                    options={saleOptions}
                    renderInput={params => (
                      <TextField {...params} placeholder="Select sale..." />
                    )}
                    renderOption={renderSaleOption}
                    value={selectedSaleOption || null}
                  />
                </SaleSelectWrapper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={onClose} type="button">
            Cancel
          </SecondaryButton>
          <Button
            disabled={!selectedDeploymentSaleId || scans.length === 0}
            onClick={onSubmit}
            type="button"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        title="Confirm Move Scans"
        isOpen={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        onDelete={onConfirmMoveScans}
        message={confirmMessage}
        buttonMessage={confirmButtonMessage}
      />
    </>
  );
}
